import { Badge, Flex, HStack } from '@chakra-ui/react';
import type WebUser from 'src/auth/WebUser';
import { useI18n } from 'src/i18n/I18nContext';
import { ColorModeButton } from '@/components/ui/color-mode';
import { Button } from '@/components/ui/button';
import {
    MenuContent,
    MenuRoot,
    MenuSeparator,
    MenuTrigger,
} from '@/components/ui/menu';
import LanguageSelect from '../fields/LanguageSelect';
import TimerButton from '../timer/TimerButton';
import HeaderBrand from './HeaderBrand';
import ProfileMenu from './HeaderProfile';
import {
    DesktopNavButton,
    HeaderDropdownItem,
    HeaderNavigationBadge,
} from './NavigationPrimitives';
import {
    getMenuNodeBadgeCount,
    getAllowedMenuNodes,
    isHeaderLinkNode,
    isMenuNodeActive,
} from './navigation';
import type { HeaderBadgeCounts, HeaderMenuNode } from './navigation';

interface DesktopHeaderProps {
    user: WebUser | null;
    menuNodes: HeaderMenuNode[];
    badgeCounts?: HeaderBadgeCounts;
    path: string;
    isLoginPage: boolean;
    isTestEnvironment: boolean;
    onNavigate: (link: string) => void;
    onLogout: () => void;
}

const DesktopNavigation = ({
    user,
    menuNodes,
    badgeCounts = {},
    path,
    onNavigate,
}: Pick<DesktopHeaderProps, 'user' | 'menuNodes' | 'badgeCounts' | 'path' | 'onNavigate'>) => {
    const { t } = useI18n();
    const allowedNodes = getAllowedMenuNodes(menuNodes, user?.data.role);

    return (
        <HStack
            gap='1'
            p='1'
            rounded='full'
            bg='bg.subtle'
            borderWidth='1px'
            borderColor='border.subtle'
        >
            {allowedNodes.map((node, nodeIndex) => {
                const active = isMenuNodeActive(node, path);
                const badgeCount = getMenuNodeBadgeCount(node, badgeCounts);

                if (isHeaderLinkNode(node)) {
                    return (
                        <DesktopNavButton
                            key={node.nameKey}
                            active={active}
                            showChevron={false}
                            aria-current={active ? 'page' : undefined}
                            onClick={() => onNavigate(node.link)}
                        >
                            {t(node.nameKey)}
                            <HeaderNavigationBadge count={badgeCount} />
                        </DesktopNavButton>
                    );
                }

                return (
                    <MenuRoot key={node.nameKey} positioning={{ gutter: 8 }}>
                        <MenuTrigger asChild>
                            <DesktopNavButton active={active}>
                                {t(node.nameKey)}
                                <HeaderNavigationBadge count={badgeCount} />
                            </DesktopNavButton>
                        </MenuTrigger>
                        <MenuContent
                            minW='18rem'
                            p='2'
                            rounded='xl'
                            borderColor='border.subtle'
                            bg='bg.panel'
                            shadow='2xl'
                        >
                            {getAllowedMenuNodes(node.nodes ?? [], user?.data.role).map((child, childIndex) =>
                                isHeaderLinkNode(child) ? (
                                    <HeaderDropdownItem
                                        key={`menu-${nodeIndex}-${childIndex}`}
                                        node={child}
                                        role={user?.data.role}
                                        path={path}
                                        badgeCount={getMenuNodeBadgeCount(child, badgeCounts)}
                                        onNavigate={onNavigate}
                                    />
                                ) : (
                                    <MenuSeparator key={`separator-${nodeIndex}-${childIndex}`} my='1' />
                                )
                            )}
                        </MenuContent>
                    </MenuRoot>
                );
            })}
        </HStack>
    );
};

const DesktopHeader = ({
    user,
    menuNodes,
    badgeCounts,
    path,
    isLoginPage,
    isTestEnvironment,
    onNavigate,
    onLogout,
}: DesktopHeaderProps) => {
    const { t } = useI18n();

    return (
        <Flex
            display={{ base: 'none', lg: 'flex' }}
            minH='4.25rem'
            align='center'
            gap='3'
        >
            <HStack gap='3' flexShrink={0}>
                <HeaderBrand user={user?.data ?? null} variant='desktop' onClick={() => onNavigate('/')} />
                {isTestEnvironment && (
                    <Badge colorPalette='red' variant='subtle' rounded='full'>
                        Test
                    </Badge>
                )}
            </HStack>

            <Flex flex='1' justify='left' minW='0'>
                <DesktopNavigation
                    user={user}
                    menuNodes={menuNodes}
                    badgeCounts={badgeCounts}
                    path={path}
                    onNavigate={onNavigate}
                />
            </Flex>

            <HStack gap='2' flexShrink={0}>
                {user && <TimerButton variant='desktop' role={user?.data.role} />}
                {user ? (
                    <ProfileMenu
                        user={user}
                        path={path}
                        variant='desktop'
                        onNavigate={onNavigate}
                        onLogout={onLogout}
                    />
                ) : isLoginPage ? null : (
                    <Button
                        colorPalette='blue'
                        size='sm'
                        rounded='full'
                        px='5'
                        onClick={() => onNavigate('/login/')}
                    >
                        {t('header.login')}
                    </Button>
                )}
                <LanguageSelect />
                <ColorModeButton
                    rounded='full'
                    borderWidth='1px'
                    borderColor='border.subtle'
                    bg='bg.panel'
                />
            </HStack>
        </Flex>
    );
};

export default DesktopHeader;
